import { cn } from '@/lib/cn';

/* ────────────────────────────────────────────────────────────────────────────
   Loading placeholders. The shimmer lives on a child layer so the base tint and
   the moving gradient never fight over `background`.
   ──────────────────────────────────────────────────────────────────────────── */

export interface SkeletonProps {
  variant?: 'text' | 'block' | 'circle';
  className?: string;
}

const VARIANTS = {
  text: 'h-3.5 w-full rounded',
  block: 'h-16 w-full rounded-lg',
  circle: 'h-9 w-9 rounded-full',
} as const;

export function Skeleton({ variant = 'block', className }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative block shrink-0 overflow-hidden bg-elevated',
        VARIANTS[variant],
        className,
      )}
    >
      <span className="absolute inset-0 animate-shimmer bg-shimmer bg-[length:220%_100%] bg-no-repeat" />
    </span>
  );
}

export interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

/** Paragraph placeholder — the last line is short so it reads as prose. */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  const count = Math.max(1, lines);
  return (
    <div className={cn('flex w-full flex-col gap-2', className)} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <Skeleton
          key={i}
          variant="text"
          className={i === count - 1 && count > 1 ? 'w-3/5' : 'w-full'}
        />
      ))}
    </div>
  );
}
